import { useEffect, useRef, useState, memo } from 'react';

declare global {
  interface Window {
    TradingView: any;
  }
}

interface TradingViewWidgetProps {
  symbol?: string;
}

const TradingViewWidget = ({ symbol = "BTCUSDT" }: TradingViewWidgetProps) => {
  const container = useRef<HTMLDivElement>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading');

  useEffect(() => {
    setStatus('loading');
    // Clean up any existing script to prevent duplicates
    if (scriptRef.current) {
      scriptRef.current.remove();
    }

    // Format symbol for QuickSwap pairs
    // QuickSwap pairs are typically in format like QUICK/MATIC or TOKEN/MATIC
    const normalizedSymbol = symbol.replace(/^\$/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const formattedSymbol = `QUICKSWAP:${normalizedSymbol}MATIC`;
    console.log('Loading TradingView with symbol:', formattedSymbol);
    
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/tv.js";
    script.type = "text/javascript";
    script.async = true;
    scriptRef.current = script;

    const unavailableTimer = window.setTimeout(() => setStatus('unavailable'), 10000);
    const onScriptLoad = () => {
      if (container.current && window.TradingView) {
        clearTimeout(unavailableTimer);
        setStatus('ready');
        new window.TradingView.widget({
          autosize: true,
          symbol: formattedSymbol,
          interval: "D",
          timezone: "Etc/UTC",
          theme: "dark",
          style: "1",
          locale: "en",
          toolbar_bg: "#f1f3f6",
          enable_publishing: false,
          allow_symbol_change: true,
          container_id: container.current.id,
          hide_side_toolbar: false,
          studies: ["RSI@tv-basicstudies"],
          show_popup_button: true,
          popup_width: "1000",
          popup_height: "650",
        });
      } else {
        setStatus('unavailable');
      }
    };

    script.addEventListener('load', onScriptLoad);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', onScriptLoad);
      if (scriptRef.current) {
        scriptRef.current.remove();
      }
      clearTimeout(unavailableTimer);
    };
  }, [symbol]);

  const containerId = `tradingview_${symbol.replace(/[^a-zA-Z0-9]/g, '_')}`;

  return (
    <div className="relative h-full w-full">
      <div id={containerId} ref={container} className="h-full w-full" />
      {status === 'loading' && <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground bg-card">Loading market chart…</div>}
      {status === 'unavailable' && <div className="absolute inset-0 flex items-center justify-center text-center text-sm text-muted-foreground bg-card px-4">No public QuickSwap chart is available for this token yet.</div>}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(TradingViewWidget);
